import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Store } from 'redux'

/** All the method for HTTP request */
type IHttpMethods = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** All the HTTP method props */
interface IHttpFuncProps {
  /** Endpoint of you API */
  endpoint: string
  /** Method for your API */
  method: IHttpMethods
  /** Body params for API */
  params: any
  /** Headers for API */
  headers: any
}

/*
 * @param {String} url
 * @param {variable} variables
 * @returns {string} updated url
 * this method take url and replace inner url with giving variable object
 */
const generateDynamicEndpoint = (url: string, variables: any) => {
  Object.keys(variables || {}).map((i) => {
    url = url.replace(`{${i}}`, variables[i])
  })

  return url
}

/**
 * @param {object}
 * @returns function call api and return promise. once this promise fullfil it will return data
 */
const defaultHttpFn = ({
  endpoint,
  method,
  params,
  headers,
  ...other
}: IHttpFuncProps) => {
  const parseMethod: string = (method || 'GET').toUpperCase()

  if (parseMethod === 'GET' && params && typeof params === 'object') {
    const urlParams = new URLSearchParams(params)
    endpoint += `?${urlParams.toString()}`
  }

  const requestOptions: any = {
    method: parseMethod,
    headers: {
      ...(headers || {})
    },
    ...(other || {})
  }

  if (params) {
    if (params instanceof FormData) {
      requestOptions.body = params
    } else {
      requestOptions.headers['Content-Type'] = 'application/json'
      requestOptions.body = JSON.stringify(params)
    }
  }

  return window.fetch(endpoint, requestOptions).then(async (res) => {
    const contentType = res.headers.get('Content-Type')
    const statusCode = res.status

    let data
    if (contentType && contentType.includes('application/json')) {
      data = await res.json() // Parse the JSON response
    } else if (contentType && contentType.includes('text/html')) {
      data = await res.text() // Treat the response as HTML content
    } else {
      data = res
    }

    return {
      status: statusCode,
      data: data
    }
  })
}

/** Check if any httpFn Defined by user then use that other wise use defaultHttpFn */
const makeHttpCall = (context: any, params: any) => {
  if (context.httpFn) {
    return context.httpFn(params)
  } else {
    return defaultHttpFn(params)
  }
}

/** Interface used for all the allowed props used to generate base instance */
interface ICreateBase {
  /** Redux store of your project */
  store: Store
  /** Base endpoint url of your backend */
  baseApiUrl: string
  /** Redux Dispatch */
  dispatch: Function
  /** Custom httpFn: Used when you make http request */
  httpFn: Function
  /** Middleware functions: executed before/after of every HTTP request. */
  middleware: any
  /** Name of your redux where you wanted to save data.
   * Can be update later for different HTTP Request.
   */
  reducer: 'string'
  /** Default state for your HTTP Request.
   * Can be update later for different HTTP Request.
   */
  defaultState: any

  /** Function used to validate the http response. */
  validateHttpResponse: Function
}

/** Interface used for all the allowed props used to generate api instance */
interface IApiInstance extends ICreateBase {
  /** Endpoint of your HTTP Request */
  endpoint: string
  /** Redux name where you want to save result of HTTP Request. */
  reduxKey: string
  /**
   * Different version of action generated automatically.
   */
  action: string
  /** Unique key from your record
   * can be 'id' or 'parent.child.sub-child' or 'parent[0].child.sub-child'
   */
  key: string
  /** HTTP Request Method
   * GET | Post | PUT | DELETE
   */
  method: IHttpMethods
  /** Callback function executed when HTTP Request Succeed. */
  onSuccess: Function

  /** Callback function executed when HTTP Request Failed. */
  onError: Function
}

interface IApiListInstance extends IApiInstance {
  /**
   * path to your list
   */
  listPath: string
}

/** Interface for middleware function */
interface IMiddleware {
  /** Callback function executed before HTTP Request happens
   * return result will be passed and saved to redux
   */
  beforeStart?: Function
  /** Callback function executed only success of HTTP Request
   * return result will be passed and saved to redux
   */
  afterSuccess?: Function

  onError?: Function

  afterUpdate?: Function

  afterDelete?: Function
}

/** CreateBase is used to generate Base Instance */
class CreateBase {
  store: any
  dispatch: Function
  baseApiUrl: string
  httpFn: Function
  middleware: IMiddleware
  reducer: string
  defaultState: any
  validateHttpResponse: Function
  other: any

  constructor(data: ICreateBase) {
    const {
      store,
      baseApiUrl,
      httpFn,
      middleware,
      reducer,
      defaultState,
      validateHttpResponse,
      ...other
    } = data
    this.store = store
    this.dispatch = store.dispatch
    this.baseApiUrl = baseApiUrl || ''
    this.httpFn = httpFn
    this.middleware = middleware
    this.reducer = reducer
    this.defaultState = defaultState
    this.validateHttpResponse = validateHttpResponse
    this.other = other
  }

  /** Used to create HTTP instance from the Base instance */
  createInstance(data: IApiInstance) {
    return new API_INSTANCE({
      ...this.other,
      ...data,
      store: data.store || this.store,
      dispatch: data.dispatch || this.dispatch,
      baseApiUrl: data.baseApiUrl || this.baseApiUrl || '',
      httpFn: data.httpFn || this.httpFn,
      defaultState: data.defaultState || this.defaultState,
      validateHttpResponse:
        data.validateHttpResponse || this.validateHttpResponse
    })
  }

  /** Used to create Listing HTTP instance from the Base instance */
  createListInstance(data: IApiListInstance) {
    return new LISTING_API_INSTANCE({
      ...this.other,
      ...data,
      store: data.store || this.store,
      dispatch: data.dispatch || this.dispatch,
      baseApiUrl: data.baseApiUrl || this.baseApiUrl || '',
      httpFn: data.httpFn || this.httpFn,
      defaultState: data.defaultState || this.defaultState,
      validateHttpResponse:
        data.validateHttpResponse || this.validateHttpResponse,
      listPath: data.listPath
    })
  }
}

interface IReduxActions {
  /** <reducerName>_DEFAULT_<yourReduxKey> */
  default: string
  /** <reducerName>_START_<yourReduxKey> */
  start: string
  /** <reducerName>_SUCCESS_<yourReduxKey> */
  success: string
  /** <reducerName>_UPDATE_<yourReduxKey> */
  update: string
  /** <reducerName>_DELETE_<yourReduxKey> */
  delete: string
  /** <reducerName>_ERROR_<yourReduxKey> */
  error: string
}

interface IOtherProps {
  /** Used to refetch data even changing loading to true. */
  refetch?: Boolean
  /** Used to pass Body of HTTP request. */
  params?: any
  /** Used to replace dynamic portion of api endpoint
   * /api/v2/user/:id
   * in above ":id" will be replaced with value of id key from the urlParams object.
   */
  urlParams?: any

  /**
   * HTTP Header
   */
  headers?: any
}

/** Class used to create HTTP method */
class API_INSTANCE {
  store: any
  dispatch: Function
  baseApiUrl: string
  httpFn: Function
  middleware: IMiddleware
  reducer: string
  defaultState: any
  other: IOtherProps
  reduxKey: string
  actions: IReduxActions
  endpoint: string
  key: string
  apiMethod: IHttpMethods
  onSuccess: Function
  state: any
  validateHttpResponse: Function
  onError: any

  /** Generate dynamic action with combination of different situation. */
  generateActions(ac: string, reducer: string) {
    this.actions = {
      default: `${reducer}_default_` + ac,
      start: `${reducer}_START_` + ac,
      success: `${reducer}_SUCCESS_` + ac,
      update: `${reducer}_UPDATE_` + ac,
      delete: `${reducer}_DELETE_` + ac,
      error: `${reducer}_ERROR_` + ac
    }
  }

  /** Used to get current redux state from the store. */
  getCurrentState() {
    const state = this.store.getState()?.[this.reducer]?.[this.reduxKey]

    if (state) {
      return JSON.parse(JSON.stringify(state))
    }

    return state
  }

  constructor({
    endpoint,
    reduxKey,
    action,
    key,
    method,
    onSuccess,
    onError,
    store,
    dispatch,
    baseApiUrl,
    httpFn,
    defaultState,
    middleware,
    reducer,
    validateHttpResponse
  }: IApiInstance) {
    this.store = store
    this.dispatch = dispatch
    this.baseApiUrl = baseApiUrl
    this.httpFn = httpFn
    this.defaultState = defaultState
    this.middleware = middleware
    this.reducer = reducer
    this.generateActions(action, reducer)
    this.reduxKey = reduxKey
    this.endpoint = endpoint
    this.key = key
    this.apiMethod = method || 'GET'
    this.onSuccess = onSuccess
    this.onError = onError
    this.validateHttpResponse = validateHttpResponse
  }

  dispatchErrorEvent(response: any, apiState: any = {}): any {
    let onErrorResponse = response
    if (typeof this.onError === 'function') {
      const cbPayload = this.onError(response, apiState)
      if (cbPayload !== undefined) {
        onErrorResponse = cbPayload
      }
    }

    onErrorResponse = {
      loading: false,
      error: onErrorResponse,
      event: 'error'
    }

    if (typeof this?.middleware?.onError === 'function') {
      const cbPayload: any = this.middleware?.onError(
        {
          currentState: this.getCurrentState(),
          instance: this,
          response: onErrorResponse,
          event: 'error'
        },
        apiState
      )
      if (cbPayload !== undefined) {
        onErrorResponse = cbPayload
      }
    }

    this.dispatch({
      type: this.actions.error,
      key: this.reduxKey || 'unknown',
      payload: onErrorResponse,
      event: 'error'
    })
    return onErrorResponse
  }

  /** Used to fire HTTP request. */
  call(
    other: IOtherProps = {
      refetch: false,
      params: {},
      urlParams: {},
      headers: {}
    }
  ) {
    let startActionPayload = {
      ...this.getCurrentState(),
      ...(!other.refetch && {
        loading: true
      }),
      error: {},
      event: 'start'
    }

    if (typeof this?.middleware?.beforeStart === 'function') {
      startActionPayload = this.middleware.beforeStart(
        {
          currentState: this.getCurrentState(),
          instance: this,
          params: other.params,
          urlParams: other.urlParams,
          event: 'start'
        },
        other
      )
    }

    this.dispatch({
      type: this.actions.start,
      key: this.reduxKey || '',
      payload: startActionPayload,
      event: 'start'
    })

    try {
      const apiState = {
        endpoint: generateDynamicEndpoint(
          getMergedEndpoint(this.baseApiUrl, this.endpoint),
          other.urlParams || {}
        ),
        method: this.apiMethod,
        headers: other.headers,
        ...(other.params && {
          params: other.params
        })
      }

      const apiPromise = makeHttpCall(this, apiState)

      apiPromise
        .then(async ({ data: response, status = 200 }: any) => {
          let isValidResponse = true
          if (this.validateHttpResponse) {
            const cbValidateResponse = this.validateHttpResponse(response)
            if (!cbValidateResponse) {
              isValidResponse = false
            }
          } else if (status > 299) {
            isValidResponse = false
          }

          if (!isValidResponse) {
            return this.dispatchErrorEvent(response, other)
          }

          if (typeof this.onSuccess === 'function') {
            const onSuccessResult = this.onSuccess(response, {
              ...apiState,
              ...other
            })
            if (onSuccessResult !== undefined) {
              response = onSuccessResult
            }
          }

          let newPayload = {
            ...this.getCurrentState(),
            loading: false,
            data: response,
            event: 'success'
          }

          if (typeof this?.middleware?.afterSuccess === 'function') {
            const cbPayload = this.middleware?.afterSuccess(
              {
                currentState: this.getCurrentState(),
                instance: this,
                response: response,
                event: 'success'
              },
              {
                ...apiState,
                ...other
              }
            )
            if (cbPayload !== undefined) {
              newPayload = cbPayload
            }
          }

          this.dispatch({
            type: this.actions.success,
            key: this.reduxKey || 'unknown',
            payload: newPayload,
            event: 'success'
          })
          return response
        })
        .catch((error: any) => {
          return this.dispatchErrorEvent(error, {
            ...other,
            ...apiState
          })
        })
    } catch (error) {
      return this.dispatchErrorEvent(error, {
        ...other
      })
    }
  }

  /** Used to refetch data without making loading flags to true. */
  refetchAction(other = {}) {
    this.call({ ...other, refetch: true })
  }
}

/** Used to extract value from the object or array or any complex variable
 * path can be 'id' or 'parent.child.sub-child' or 'parent[0].child.sub-child'
 */
const getValueFromPath = (data: any, path: string): string | undefined => {
  path = path.replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
  path = path.replace(/^\./, '') // strip a leading dot
  var a = path.split('.')
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i]
    if (k in data) {
      data = data[k]
    } else {
      return undefined
    }
  }
  return data
}

const setValueFromPath = (data: any, path: string, value: any) => {
  path = path.replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
  path = path.replace(/^\./, '') // strip a leading dot

  var a = path.split('.')
  var o = data
  while (a.length - 1) {
    var n: any = a.shift()
    if (!(n in o)) o[n] = {}
    o = o[n]
  }
  o[a[0]] = value
}

/** It merges the baseApiUrl with current endpoint path */
const getMergedEndpoint = (baseApiUrl: string, endpoint: string) => {
  if (!baseApiUrl) return endpoint

  if (endpoint[0] === '/') {
    return baseApiUrl + endpoint
  }

  return endpoint
}

/**
 *
 * @param {Array} arr
 * @param {Array} deleteArr
 * @param {Object} options
 * @returns will remove record from the arr based on values of deleteArr based on key or callback passed in options
 */
const removeRecordsFromArray = (
  arr: any = [],
  deleteArr: any,
  options: { key?: string; callback: Function }
) => {
  const { key = '', callback } = options
  const deletedFullArray: any = []

  arr = arr.filter((item: any) => {
    const id = callback ? callback(item) : item[key]
    if (deleteArr.includes(id)) {
      deletedFullArray.push(item)
      return false
    } else {
      return true
    }
  })

  return {
    result: arr,
    deleted: deletedFullArray.length,
    deletedItems: deletedFullArray
  }
}

/**
 *
 * @param {Array} array
 * @param {Object} options
 * @returns used to removing duplicate items from array based on key or callback passed in options,
 */
const removeDuplicateFromArray = (
  array: Array<object>,
  options: { key?: string; callback?: Function; excludeSecond?: Boolean }
) => {
  const { key, callback, excludeSecond = false } = options
  const newList = {}
  let duplicates = 0

  array.forEach((item) => {
    let id = callback ? callback(item) : key ? item[key] : ''
    id = id + 'p'
    if (newList[id]) {
      duplicates++
      if (excludeSecond === false) {
        newList[id] = item
      }
    } else {
      newList[id] = item
    }
  })

  return { result: Object.values(newList || {}), duplicates: duplicates }
}

/** It contain all the feature from API_INSTANCE with additional method to delete and update records from the list in redux. */
class LISTING_API_INSTANCE extends API_INSTANCE {
  listPath: string

  constructor(data: IApiListInstance) {
    super(data)
    this.listPath = data.listPath
  }

  updateAction(data: Array<any> | any) {
    try {
      let state = this.getCurrentState()

      let listFromPath: any = []
      if (this.listPath) {
        const pathValue = getValueFromPath(state, this.listPath)
        if (!listFromPath) {
          listFromPath = []
        } else if (!Array.isArray(pathValue)) {
          throw new Error('listPath should be path to array state')
        } else {
          listFromPath = pathValue
        }
      } else if (!Array.isArray(state.data)) {
        throw new Error(
          'data should be array state. if your list is not in data state then you need to pass listPath in instance'
        )
      }

      const list = [
        ...(this.listPath ? getValueFromPath(state, this.listPath) : state.data)
      ]

      const result = removeDuplicateFromArray(
        [...list, ...(Array.isArray(data) ? data : [data])],
        {
          key: this.key
        }
      )

      if (typeof this?.middleware?.afterUpdate === 'function') {
        state = this.middleware.afterUpdate({
          currentState: this.getCurrentState(),
          instance: this,
          updateData: result
        })
      } else if (this.listPath) {
        setValueFromPath(
          state,
          this.listPath,
          Object.values(result.result || {})
        )
      } else {
        state.data = Object.values(result.result || {})
      }

      this.dispatch({
        type: this.actions.update,
        key: this.reduxKey,
        payload: {
          ...this.getCurrentState(),
          ...state,
          event: 'update'
        },
        event: 'update'
      })
    } catch (error) {
      showErrorMessage(error)
    }
  }

  deleteAction(id: string[] | string | number) {
    try {
      let state = this.getCurrentState()

      let listFromPath: any = []
      if (this.listPath) {
        const pathValue = getValueFromPath(state, this.listPath)
        if (!listFromPath) {
          listFromPath = []
        } else if (!Array.isArray(pathValue)) {
          throw new Error('listPath should be path to array state')
        } else {
          listFromPath = pathValue
        }
      } else if (!Array.isArray(state.data)) {
        throw new Error(
          'data should be array state. if your list is not in data state then you need to pass listPath in instance'
        )
      }

      const list = [
        ...(this.listPath ? getValueFromPath(state, this.listPath) : state.data)
      ]

      const deleteData = removeRecordsFromArray(
        list,
        Array.isArray(id) ? id : [id],
        {
          callback: (i: any) => getValueFromPath(i, this.key || '')
        }
      )

      if (typeof this?.middleware?.afterDelete === 'function') {
        state = this.middleware.afterDelete({
          currentState: this.getCurrentState(),
          instance: this,
          deleteData: deleteData
        })
      } else if (this.listPath) {
        setValueFromPath(state, this.listPath, deleteData.result)
      } else {
        state.data = deleteData.result
      }

      this.dispatch({
        type: this.actions.delete,
        key: this.reduxKey,
        payload: {
          ...this.getCurrentState(),
          ...state,
          event: 'delete'
        },
        event: 'delete'
      })
    } catch (error) {
      showErrorMessage(error)
    }
  }
}

const showErrorMessage = (message: Error) => {
  console.log(
    `%c ${message.message} `,
    ' font-size: 12px; color: #bada55; background: #222;',
    message
  )
}

/** This method is used to register dynamic action which is being call from all the instances. */
const bindReducer = (
  reducer: (state: any, action: 'string', ...other: any[]) => {},
  config: { key: '' }
): any => {
  const path = config.key + '_'

  return (state: any, action: any, ...other: any) => {
    if (action.type.includes(path)) {
      return {
        ...state,
        [action.key]: {
          ...(action.payload || {}),
          ...(action.event && {
            event: action.event
          })
        }
      }
    } else {
      return reducer(state, action, ...other)
    }
  }
}

/** Used to get current state of the HTTP request where detail will be store */
const useInstance = (instance: IApiInstance): any => {
  const currentState = useSelector(
    (state: any) => state?.[instance.reducer]?.[instance.reduxKey]
  )

  const memoedState = useMemo(() => {
    let state = currentState || instance.defaultState
    state = JSON.parse(JSON.stringify(state))
    return state
  }, [currentState])

  return memoedState
}

// ## useInstance

// use useInstance hook which help you get easily fetch state of current HTTP request. you don't have to make any configuation.

// ```
// const reduxState = useInstance(httpInstance);
// ```
// reduxState contain value of given(httpInstance) instance state from the redux.

interface IAIEvents {
  onStart?: Function
  onSuccess?: Function
  onError?: Function
  onUpdate?: Function
  onDelete?: Function
}

const useAIEventHandler = (instance: IApiInstance, events: IAIEvents) => {
  const isComponentLoaded = useRef(false)

  // const
  const state = useInstance(instance)

  useEffect(() => {
    if (isComponentLoaded.current) {
      if (state.loading === true && state.event === 'start') {
        events.onStart && events.onStart(state)
      }
      if (state.loading === false && state.data && state.event === 'success') {
        events.onSuccess && events.onSuccess(state)
      }
      if (state.loading === false && state.error && state.event === 'error') {
        events.onError && events.onError(state)
      }
      if (state.loading === false && state.event === 'update') {
        events.onUpdate && events.onUpdate(state)
      }
      if (state.loading === false && state.event === 'delete') {
        events.onUpdate && events.onUpdate(state)
      }
    }
    if (!isComponentLoaded.current) {
      isComponentLoaded.current = true
    }
  }, [state])

  return null
}

export {
  CreateBase,
  API_INSTANCE,
  generateDynamicEndpoint,
  removeRecordsFromArray,
  removeDuplicateFromArray,
  bindReducer,
  useInstance,
  useAIEventHandler
}

// let state = AI_getCompanyList.store.getState()?.[AI_getCompanyList.reducer]?.[AI_getCompanyList.reduxKey]

// const removeDuplicateFromArray = (array, options) => {
//   const { key, callback, excludeSecond = false } = options
//   const newList = {}
//   let duplicates = 0

//   array.forEach(item => {
//     let id = callback ? callback(item) : key ? item[key] : ""
//     id = id + "p"
//     if (newList[id]) {
//       duplicates++
//       if (excludeSecond === false) {
//         newList[id] = item
//       }
//     } else {
//       newList[id] = item
//     }
//   })

//   return { result: Object.values(newList || {}), duplicates: duplicates }
// }

// const getValueFromPath = (data, path) => {
//   path = path.replace(/\[(\w+)\]/g, ".$1") // convert indexes to properties
//   path = path.replace(/^\./, "") // strip a leading dot
//   var a = path.split(".")
//   for (var i = 0, n = a.length; i < n; ++i) {
//     var k = a[i]
//     if (k in data) {
//       data = data[k]
//     } else {
//       return ""
//     }
//   }
//   return data
// }

// const setValueFromPath = (data, path, value) => {
//   path = path.replace(/\[(\w+)\]/g, ".$1") // convert indexes to properties
//   path = path.replace(/^\./, "") // strip a leading dot

//   var a = path.split(".")
//   var o = data
//   while (a.length - 1) {
//     var n = a.shift()
//     if (!(n in o)) o[n] = {}
//     o = o[n]
//   }
//   o[a[0]] = value
// }

// let listPath = "data.rows"

// let list = getValueFromPath(AI_getCompanyList.getCurrentState(), listPath)

// let data = {
//     "location_name": [
//         "asdfas1sdf"
//     ],
//     "id": 2356,
//     "company_name": "asdfasdf",
//     "updatedAt": "2023-05-01T09:50:36.147Z",
//     "createdAt": "2023-05-01T09:50:36.147Z"
// }

// const result = removeDuplicateFromArray(
//   [...list, ...(Array.isArray(data) ? data : [data])],
//   {
//     key: "id"
//   }
// )

// setValueFromPath(
//   state,
//   listPath,
//   Object.values(result.result || {})
// )
